import React, { useEffect, useState } from "react";
import { Table, Spinner, Button, Input, Form, FormGroup, Label } from "reactstrap";
import axiosInstance from "../../Request";
import { URL } from "../../api";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const EMIReport = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filterType, setFilterType] = useState("customerId");
  const [filterValue, setFilterValue] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const getTodayDateISO = () => new Date().toISOString().split("T")[0];

  const fetchEMIReport = async (filters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams(filters).toString();
      const url = `${URL.REPORTING_EMI}${params ? `?${params}` : ""}`;
      const response = await axiosInstance.get(url);
      setRows(response.data || []);
    } catch (err) {
      console.error("Error fetching EMI report:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initially load today's EMI data when no filters
    fetchEMIReport({});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const filters = {};
    const todayISO = getTodayDateISO();

    // Validate dates
    if (fromDate && fromDate > todayISO) {
      alert("From Date cannot be in the future!");
      return;
    }
    if (toDate && toDate > todayISO) {
      alert("To Date cannot be in the future!");
      return;
    }

    // Set customerId or loanId based on selected filter
    if (filterValue.trim()) {
      filters[filterType] = filterValue.trim();
    }

    // Add date range filters
    if (fromDate && toDate) {
      if (fromDate > toDate) {
        alert("From Date cannot be after To Date.");
        return;
      }
      filters.fromDate = fromDate; // Use correct API param
      filters.toDate = toDate;
    }

    fetchEMIReport(filters);
  };

  const exportToExcel = () => {
    if (rows.length === 0) {
      alert("No data available to export!");
      return;
    }
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "EMI_Report");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), "EMI_Report.xlsx");
  };

  const font12 = { fontSize: "10px" };
  const inputSmStyle = { ...font12, height: "30px", padding: "2px 6px" };
  const buttonSmStyle = { ...font12, padding: "4px 10px", height: "30px", lineHeight: "1" };

  const currentLabel = filterType === "customerId" ? "Customer ID" : "Loan ID";
  const tableHeaders = rows.length > 0 ? Object.keys(rows[0]) : [];

  return (
    <div style={font12}>
      {/* Search Form */}
      <Form onSubmit={handleSearch} className="mb-3">
        <div
          className="d-flex align-items-end flex-wrap"
          style={{ gap: "10px", overflowX: "auto", paddingBottom: "4px" }}
        >
          <FormGroup className="mb-0" style={{ minWidth: "130px" }}>
            <Label className="mb-1 fw-bold" style={font12}>
              Filter Type
            </Label>
            <Input
              bsSize="sm"
              type="select"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={inputSmStyle}
            >
              <option value="customerId">Customer ID</option>
              <option value="loanId">Loan ID</option>
            </Input>
          </FormGroup>

          <FormGroup className="mb-0" style={{ minWidth: "160px" }}>
            <Label className="mb-1 fw-bold" style={font12}>
              {currentLabel}
            </Label>
            <Input
              bsSize="sm"
              type="text"
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
              placeholder={`Enter ${currentLabel}`}
              style={inputSmStyle}
            />
          </FormGroup>

          <FormGroup className="mb-0" style={{ minWidth: "150px" }}>
            <Label className="mb-1 fw-bold" style={font12}>
              From Date
            </Label>
            <Input
              bsSize="sm"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              style={inputSmStyle}
            />
          </FormGroup>

          <FormGroup className="mb-0" style={{ minWidth: "150px" }}>
            <Label className="mb-1 fw-bold" style={font12}>
              To Date
            </Label>
            <Input
              bsSize="sm"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              style={inputSmStyle}
            />
          </FormGroup>

          <div className="d-flex mb-0" style={{ gap: "6px" }}>
            <Button color="primary" type="submit" style={buttonSmStyle}>
              Search
            </Button>
            <Button type="button" color="success" onClick={exportToExcel} style={buttonSmStyle}>
              Export To Excel
            </Button>
          </div>
        </div>
      </Form>

      {/* Table */}
      {loading ? (
        <div className="d-flex align-items-center mt-3" style={{ gap: 8 }}>
          <Spinner size="sm" /> <span>Loading EMI report...</span>
        </div>
      ) : rows.length === 0 ? (
        <p className="mt-3 text-danger">No EMI data found!</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <Table bordered striped hover responsive size="sm" className="mt-3 mb-0">
            <thead className="thead-light">
              <tr>
                <th style={font12}>#</th>
                {tableHeaders.map((header) => (
                  <th key={header} style={font12}>
                    {header.replace(/_/g, " ").toUpperCase()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.id || index}>
                  <td style={font12}>{index + 1}</td>
                  {tableHeaders.map((key) => (
                    <td
                      key={key}
                      style={
                        key === "emi_status_value" ? { color: "red", fontWeight: "bold" } : font12
                      }
                    >
                      {row[key] !== null && row[key] !== undefined ? row[key].toString() : "-"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default EMIReport;
